import * as React from 'react';
import { debounce } from 'throttle-debounce';
import { useEventCallback } from '../../hooks';
import { AutocompleterContext, AutocompleterContextValues } from './AutocompleterContext';

const decounceDelayFast = 300;
const decounceDelaySlow = 800;
const debounceThresholdTermLength = 4;

export type AutocompleterValue = string | string[] | (string | string[])[];

export type AutocompleterProps = {
    /**
     * The child components.
     */
    children: React.ReactNode;

    /**
     * @default null
     */
    initialSearchTerm?: string;

    /**
     * If `true`, multiple options can be selected.
     * @default false
     */
    multiSelect?: boolean;

    /**
     * Function called when a selection is added.
     */
    onAdd: (value: AutocompleterValue, title: string) => void;

    /**
     * Function called when a selection is removed.
     */
    onRemove?: (value: AutocompleterValue, title: string) => void;

    /**
     * Callback fired when fetching starts.
     */
    onFetchStart?: () => void;

    /**
     * Callback fired when fetching has completed.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onFetchComplete?: (response: any, term: string) => void;

    /**
     * Error handler fired if fetching throws an error.
     */
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    onFetchError?: (error: any) => void;

    /**
     * A function which will fetch results which correspond to the search term entered in the associated `AutocompleterField`.
     */
    fetch: (term: string) => Promise<unknown>;

    selectedValue?: AutocompleterValue;

    /**
     * A scrolling element which wraps the results. used to keep the `active` autocompleter option in view during keyboard navigation.
     */
    scrollContext?: HTMLElement;
};

export function Autocompleter(props: AutocompleterProps): React.JSX.Element {
    const {
        children,
        initialSearchTerm = null,
        multiSelect = false,
        onAdd,
        onRemove,
        fetch,
        onFetchStart,
        onFetchComplete,
        onFetchError,
        selectedValue = null,
        scrollContext,
    } = props;

    const waitingFor = React.useRef<string | null>(null);
    const fetchCancelled = React.useRef<boolean>(false);

    const inputRef = React.useRef<HTMLInputElement | null>(null);

    // We need a reference to any child autcompleter results list elements, so that we can correctly
    // determine the total number of child options.
    const resultsListRef = React.useRef<HTMLElement[]>([]);

    const [searchTerm, setSearchTerm] = React.useState<string | null>(initialSearchTerm);
    const [activeOptionId, setActiveOptionId] = React.useState<string | null>(null);
    const [activeOptionIndex, setActiveOptionIndex] = React.useState<number | null>(null);
    const [toggleOptionAtIndex, setToggleOptionAtIndex] = React.useState<number | null>(null);
    const [isWaiting, setIsWaiting] = React.useState(false);

    const resetState = () => {
        setToggleOptionAtIndex(null);
        setActiveOptionIndex(null);
        setActiveOptionId(null);
    };

    const handleFetch = React.useCallback(
        (term: string) => {
            if (fetchCancelled.current) {
                return;
            }
            waitingFor.current = term;
            setIsWaiting(true);
            if (onFetchStart) {
                onFetchStart();
            }
            fetch(term)
                .then((response: unknown) => {
                    // Only use this response if it corresponds to the latest term.
                    if (!fetchCancelled.current && term === waitingFor.current) {
                        setIsWaiting(false);
                        waitingFor.current = null;
                        resetState();
                        if (onFetchComplete) {
                            onFetchComplete(response, term);
                        }
                    }
                })
                .catch((error) => {
                    if (onFetchError) {
                        onFetchError(error);
                    }
                    throw error;
                });
        },
        [fetch, onFetchComplete, onFetchError, onFetchStart]
    );

    React.useEffect(() => {
        if (searchTerm == null) {
            return;
        }
        // If the query is short or ends with a space then execute the more impatient request.
        if (searchTerm.length < debounceThresholdTermLength || searchTerm.endsWith(' ')) {
            debounce(decounceDelayFast, handleFetch)(searchTerm);
            return;
        }
        debounce(decounceDelaySlow, handleFetch)(searchTerm);
    }, [handleFetch, searchTerm]);

    React.useEffect(() => {
        return () => {
            // Component has unmounted. Any async fetches still in progress will not be actioned.
            fetchCancelled.current = true;
        };
    }, []);

    const addSelection = React.useCallback(
        (value: AutocompleterValue, title: string) => {
            onAdd(value, title);
            resetState();

            // Refocus the input
            if (multiSelect) {
                inputRef.current?.focus();
            }
        },
        [onAdd, multiSelect]
    );

    const removeSelection = React.useCallback(
        (value: AutocompleterValue, title: string) => {
            if (!onRemove) {
                return;
            }
            onRemove(value, title);
            resetState();

            // Refocus the input
            if (multiSelect) {
                inputRef.current?.focus();
            }
        },
        [onRemove, multiSelect]
    );

    const getNumberOfOptions = (): number => {
        return resultsListRef.current.reduce((count, node) => {
            return count + node.querySelectorAll('[data-autocompleter-option="true"]').length;
        }, 0);
    };

    const handleFieldKeyDown = useEventCallback((event: React.KeyboardEvent) => {
        const total = getNumberOfOptions();

        if (total === 0) {
            return;
        }

        switch (event.key) {
            case 'ArrowUp': {
                event.preventDefault();
                const index = activeOptionIndex === null ? total : activeOptionIndex - 1;
                if (index > 0) {
                    setActiveOptionIndex(index);
                }
                break;
            }
            case 'ArrowDown': {
                event.preventDefault();
                const index = activeOptionIndex === null ? 1 : activeOptionIndex + 1;
                if (index <= total) {
                    setActiveOptionIndex(index);
                }
                break;
            }
            case 'Home':
                event.preventDefault();
                setActiveOptionIndex(1);
                break;
            case 'End':
                event.preventDefault();
                setActiveOptionIndex(total);

                break;
            case 'Enter':
                event.preventDefault();
                setToggleOptionAtIndex(activeOptionIndex);
                setActiveOptionIndex(null);
                break;
            default:
        }
    });

    // Child AutocompleterResults components register/deregister their root element when they are mounted/unmounted.
    const handleRegisterResultsList = React.useCallback((element: HTMLElement) => {
        resultsListRef.current.push(element);
    }, []);

    const handleDeregisterResultsList = React.useCallback((element: HTMLElement) => {
        resultsListRef.current = resultsListRef.current.filter((el) => el !== element);
    }, []);

    const context: AutocompleterContextValues = React.useMemo(
        () => ({
            activeOptionId,
            activeOptionIndex,
            addSelection,
            isWaiting,
            multiSelect,
            onFieldKeyDown: handleFieldKeyDown,
            removeSelection,
            searchTerm,
            selectedValue,
            setActiveOptionId,
            setActiveOptionIndex,
            setSearchTerm,
            setToggleOptionAtIndex,
            toggleOptionAtIndex,
            scrollContext,
            inputRef,
            registerResultsList: handleRegisterResultsList,
            deregisterResultsList: handleDeregisterResultsList,
        }),
        [
            activeOptionId,
            activeOptionIndex,
            addSelection,
            isWaiting,
            multiSelect,
            handleFieldKeyDown,
            removeSelection,
            searchTerm,
            selectedValue,
            toggleOptionAtIndex,
            scrollContext,
            handleRegisterResultsList,
            handleDeregisterResultsList,
        ]
    );

    return <AutocompleterContext.Provider value={context}>{children}</AutocompleterContext.Provider>;
}
